import React, { createContext, useContext, useState, useEffect } from "react";

import { useParams } from "react-router-dom";

import config from "console_config";
import {
  organizationByDescriptionIdSelector,
  canCreateProjectOrganizationsSelector,
  organizationSelector
} from "Reducers/organization";
import { useAppSelector } from "Store/hooks";

import { useCreateProject, useVerification } from "./hooks";
import {
  errorTypes,
  onboardingTypes,
  projectCreationStates,
  SetupStepperContextType,
  verificationTypes
} from "./types";

import type { Region } from "@packages/client";

const SetupStepperContext = createContext<SetupStepperContextType>({
  setupStepperValues: {
    onboardingType: onboardingTypes.default,
    organization:
      {} as SetupStepperContextType["setupStepperValues"]["organization"],
    projectName: "",
    productionEnvironment: "",
    region: {} as Region
  },
  newProjectValues: {
    errorType: errorTypes.default,
    projectId: undefined,
    projectUrl: undefined,
    publicUrl: undefined,
    logUrl: undefined,
    state: projectCreationStates.default
  },
  activities: [],
  verificationType: verificationTypes.default,
  activeStep: 0,
  isCreateProjectLoading: false,
  isOnTrial: false,
  handleGoForward: () => {},
  handleGoBackward: () => {},
  handleUpdateSetupStepperValues: () => {},
  handleCreateProject: () => {},
  checkSubscriptionStatus: () => {},
  setActiveStep: () => {},
  isLastStep: false,
  getPreselectedOrganization: () => ({}),
  credentials: {}
});

export const SetupStepperContextProvider = ({ children }) => {
  const { organizationId: paramsOrganizationId } = useParams<{
    organizationId: string;
  }>();

  const organizationByName = useAppSelector(state =>
    organizationByDescriptionIdSelector(state, {
      organizationDescriptionId: paramsOrganizationId
    })
  );
  const organizationById = useAppSelector(state =>
    organizationSelector(state, {
      organizationId: paramsOrganizationId
    })
  );
  const organizations = useAppSelector(canCreateProjectOrganizationsSelector);

  const getPreselectedOrganization = () => {
    const urlOrganization = organizationByName ?? organizationById;

    const formatedUrlOrganization = urlOrganization && {
      label: urlOrganization.label,
      value: urlOrganization.id,
      name: urlOrganization.name
    };

    const formattedOrganizations = organizations.map(({ label, id, name }) => ({
      value: id,
      label,
      name
    }));

    return formatedUrlOrganization || formattedOrganizations[0];
  };

  const [setupStepperValues, setSetupStepperValues] = useState<
    SetupStepperContextType["setupStepperValues"]
  >({
    onboardingType: onboardingTypes.default,
    organization:
      getPreselectedOrganization() ||
      ({} as SetupStepperContextType["setupStepperValues"]["organization"]),
    projectName: "",
    productionEnvironment: config.CUSTOM_DEFAULT_BRANCH || "",
    region: {} as Region
  });
  const [activeStep, setActiveStep] = useState(0);
  const [isLastStep, setIsLastStep] = useState(false);
  const {
    createProject,
    newProjectValues,
    isCreateProjectLoading,
    checkSubscriptionStatus,
    activities
  } = useCreateProject();

  const { getVerificationType, isOnTrial } = useVerification(
    setupStepperValues?.organization?.value
  );

  const [verificationType, setVerificationType] = useState<
    SetupStepperContextType["verificationType"]
  >(verificationTypes.default);
  const [credentials, setCredentials] =
    useState<SetupStepperContextType["credentials"]>();

  useEffect(() => {
    let retryTimeout;

    const getVerificationTypeRecursion = (isRefreshing: boolean) => {
      getVerificationType({ isRefreshing }).then(result => {
        if (result.type === verificationTypes.idle) {
          retryTimeout = setTimeout(
            () => getVerificationTypeRecursion(true),
            2000
          );
        } else {
          clearTimeout(retryTimeout);
        }
        setVerificationType(
          result.type as SetupStepperContextType["verificationType"]
        );
        setCredentials(result?.credentials);
      });
    };

    getVerificationTypeRecursion(false);

    return () => clearTimeout(retryTimeout);
  }, [getVerificationType]);

  function handleUpdateSetupStepperValues(
    newValues: Partial<SetupStepperContextType["setupStepperValues"]>
  ) {
    setSetupStepperValues({ ...setupStepperValues, ...newValues });
  }

  function handleCreateProject(options?: {
    shouldSkipVerifyCheck?: boolean;
    newValues?: Record<string, any>;
  }) {
    setIsLastStep(true);
    createProject(
      {
        ...setupStepperValues,
        ...(options?.newValues ?? {}),
        verificationType
      },
      { ...options, isOnTrial }
    );
  }

  function handleGoForward() {
    setActiveStep(activeStep + 1);
  }

  function handleGoBackward() {
    setActiveStep(activeStep - 1);
  }

  return (
    <SetupStepperContext.Provider
      value={{
        setupStepperValues,
        handleUpdateSetupStepperValues,
        handleCreateProject,
        verificationType,
        newProjectValues,
        activeStep,
        isCreateProjectLoading,
        isOnTrial,
        activities,
        handleGoForward,
        handleGoBackward,
        checkSubscriptionStatus,
        setActiveStep,
        isLastStep,
        getPreselectedOrganization,
        credentials: credentials!
      }}
    >
      {children}
    </SetupStepperContext.Provider>
  );
};

export function useSetupStepperContext() {
  const context = useContext(SetupStepperContext);

  if (!context) {
    throw new Error(
      "useSetupStepperContext must be withing SetupStepperContext scope"
    );
  }

  return context;
}
